import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQueries } from '@tanstack/react-query';
import { type Agent, getAgents, SPECIAL_AGENT_IDS, useAgentVisibility } from '@/entities/agent';
import { getCurrentOrganizationId, usePermissions } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { openAdaptationPicker } from '@/shared/hooks';
import { TOOL_CATEGORIES } from '@/shared/constants/category-colors';
import { ROUTES } from '@/shared/config/routes';
import { useContentStudioAllowed } from '@/shared/config/useContentStudioAllowed';
import { useIqAllowed } from '@/shared/config/useIqAllowed';
import { ADAPTATION_AGENT_ID } from '@/shared/utils/adaptation';
import { FLOW_DIGEST_AGENT_ID, openFlowDigestTool } from '@/shared/utils/flow-digest';
import { withFocusGroupsOrigin } from '@/shared/utils/focusGroupsNavigation';
import {
  OPPORTUNITIES_ROOM_AGENT_ID,
  openOpportunitiesRoom,
} from '@/shared/utils/opportunities-room';
import { useToolShortcuts } from '../../hooks/useToolShortcuts/useToolShortcuts';
import { ShortcutSlot } from './ShortcutSlot';
import { useTranslation } from 'react-i18next';

export const ToolShortcuts = () => {
  const { t } = useTranslation('springboard');
  const { t: tTools } = useTranslation('tools');

  const { slots } = useToolShortcuts();
  const navigate = useNavigate();
  const workspaceId = useCurrentWorkspaceId();
  const { canAccessFocusGroups, canAccessTestContent } = usePermissions();
  const { isAllowed: isIqAllowed, isLoading: isIqLoading } = useIqAllowed();

  // Visibility conditions mirror ToolGridV4 exactly
  const { isAllowed: isContentStudioAllowed, isLoading: isContentStudioLoading } =
    useContentStudioAllowed();
  const showContentStudio = isContentStudioAllowed && !isContentStudioLoading;
  const showIq = isIqAllowed && !isIqLoading;
  const { isAgentEnabled, isLoading: isVisibilityLoading } = useAgentVisibility(
    ['test-content', SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER],
    { workspaceId, organizationId: getCurrentOrganizationId() },
  );
  const showContentTest =
    !isVisibilityLoading && canAccessTestContent && isAgentEnabled('test-content');
  const showOpportunitiesRoom =
    !isVisibilityLoading && isAgentEnabled(SPECIAL_AGENT_IDS.DEEP_RESEARCH_OPPORTUNITY_IDENTIFIER);

  // Mirror what ToolsHub renders when selectedCategory === 'all': one query per category
  const categoryQueries = useQueries({
    queries: TOOL_CATEGORIES.map((tag) => ({
      queryKey: ['agents', { tag, includeNonVisibleAgents: false }],
      queryFn: () => getAgents({ tag, includeNonVisibleAgents: false }),
      staleTime: 5 * 60 * 1000,
    })),
  });

  // Destructure individual query data arrays to give useMemo stable dependencies
  // (the categoryQueries array reference changes every render per the TanStack rule)
  const [planData, createData, scaleData, deliverData] = categoryQueries.map((q) => q.data);

  const availableAgents = useMemo(() => {
    // Merge all category results, deduplicate by id, drop unconfigured
    const seen = new Set<string>();
    const fromBackend = [planData, createData, scaleData, deliverData]
      .flatMap((data) => data ?? [])
      .filter((agent) => {
        if (seen.has(agent.id)) return false;
        seen.add(agent.id);
        return agent.configurationStatus?.configured !== false;
      });

    // Special tools injected by ToolGridV4 — same conditions as there
    const specialTools: Agent[] = [];

    if (workspaceId) {
      specialTools.push({
        id: FLOW_DIGEST_AGENT_ID,
        title: tTools('flowDigestAgent.name'),
        tag: 'plan',
      });
    }
    if (showContentTest && workspaceId) {
      specialTools.push({
        id: 'content-test',
        title: t('dashboard.toolShortcuts.contentTest'),
        tag: 'plan',
      });
    }
    if (workspaceId && canAccessFocusGroups) {
      specialTools.push({
        id: 'focus-groups',
        title: t('dashboard.toolShortcuts.focusGroups'),
        tag: 'plan',
      });
    }
    if (showContentStudio && workspaceId) {
      specialTools.push({
        id: 'content-studio',
        title: tTools('toolsHub.toolsHubConstants.contentStudio'),
        tag: 'create',
      });
    }
    if (showIq) {
      specialTools.push({ id: 'iq', title: 'IQ', tag: 'plan' });
    }
    if (workspaceId) {
      // Adaptation is a special case external tool that doesn't map to a valid agent, but is a valid
      // shortcut target. If any other similar case is necessary, make sure to update the backend set
      // in apps/api/.../agent-shortcut.service.ts to avoid validation issues.
      specialTools.push({
        id: ADAPTATION_AGENT_ID,
        title: tTools('toolsHub.toolsHubConstants.adaptation'),
        tag: 'scale',
      });
    }
    if (showOpportunitiesRoom && workspaceId) {
      specialTools.push({
        id: OPPORTUNITIES_ROOM_AGENT_ID,
        title: tTools('toolsHub.toolsHubConstants.opportunitiesRoom'),
        tag: 'plan',
      });
    }

    return [...fromBackend, ...specialTools].sort((a, b) => a.title.localeCompare(b.title));
  }, [
    planData,
    createData,
    scaleData,
    deliverData,
    workspaceId,
    canAccessFocusGroups,
    showIq,
    showContentStudio,
    showContentTest,
    showOpportunitiesRoom,
    t,
    tTools,
  ]);

  // Per-tool launch handlers — mirrors the routing logic in ToolGridV4.
  // Any id listed here overrides the default launchAgentWithWidget fallback in ShortcutSlot.
  const launchHandlers = useMemo<Partial<Record<string, () => void>>>(() => {
    const handlers: Partial<Record<string, () => void>> = {};

    if (workspaceId) {
      handlers[FLOW_DIGEST_AGENT_ID] = () => openFlowDigestTool(workspaceId);
    }
    if (workspaceId) {
      handlers[ADAPTATION_AGENT_ID] = () => openAdaptationPicker();
    }
    handlers[OPPORTUNITIES_ROOM_AGENT_ID] = () => openOpportunitiesRoom(navigate);
    if (workspaceId) {
      handlers['content-test'] = () => void navigate(ROUTES.TOOLS_CONTENT_TEST);
    }
    if (workspaceId && canAccessFocusGroups) {
      handlers['focus-groups'] = () =>
        void navigate(ROUTES.TOOLS_FOCUS_GROUPS, withFocusGroupsOrigin(ROUTES.HOME));
    }

    // IQ is a page, not a chat agent — navigate instead of launching a widget.
    // Route access is guarded by ProtectedRoute (canAccessIq) + ManagedToolGuard.
    if (showIq) {
      handlers['iq'] = () => void navigate(ROUTES.IQ);
    }

    if (showContentStudio && workspaceId) {
      handlers['content-studio'] = () => void navigate(ROUTES.CONTENT_STUDIO);
    }

    return handlers;
  }, [navigate, workspaceId, canAccessFocusGroups, showIq, showContentStudio]);

  return (
    // No heading and no surface of its own: in the design the shortcuts sit
    // beside the greeting card as bare tiles, so the tiles are the surfaces.
    <div className="grid h-full auto-rows-[150px] grid-cols-1 gap-grid sm:grid-cols-4">
      {slots.map((shortcut, index) => (
        <ShortcutSlot
          key={index}
          slotIndex={index}
          agentId={shortcut?.agentId ?? null}
          disabled={index > 0 && slots[index - 1] === null}
          slots={slots}
          availableAgents={availableAgents}
          launchHandlers={launchHandlers}
        />
      ))}
    </div>
  );
};
